import {
	clearImportCategoryMap,
	clearImportRowOverride,
	saveImportCategoryMap,
	saveImportRowOverride,
} from '../../../redux/actions/import.js';

/**
 * The translation between what the category picker hands back and the rule that gets stored.
 *
 * It lives apart from ImportCategoryMapper.jsx because it is the part worth pinning down in a test:
 * a picker value is a category id, one of the action strings, or the blank option, and which of the
 * three it is decides whether a rule is written, replaced or removed. Getting that wrong is quiet --
 * the screen still looks like it did something.
 */

/**
 * The decisions a category cannot express. Strings where a category id is a number, so the two
 * kinds of value can never be mistaken for one another in the same picker.
 *
 * Keep the wording in step with Import_Category_Rule's action constants.
 */
export const RULE_RAW_ONLY = 'raw_only';
export const RULE_SKIP = 'skip';
/**
 * A bank record and a budget transaction with no category, to be filed later in the budget. Only ever
 * picked here, by a person: no row gets it for want of a rule.
 */
export const RULE_UNCATEGORIZED = 'uncategorized';

/** Every action that is not an assignment, and so is saved with no category. */
export const NON_CATEGORY_RULES = [RULE_UNCATEGORIZED, RULE_RAW_ONLY, RULE_SKIP];

/** CategorySelect's own blank option. Its value is 0, which is unknownCategory's id. */
export const RULE_NONE = 0;

export const EXTRA_OPTION_GROUPS = [{
	label: 'Import',
	options: [
		{ label: 'Leave uncategorized', value: RULE_UNCATEGORIZED, color: 'Black' },
		{ label: 'Bank record only', value: RULE_RAW_ONLY, color: 'Black' },
		{ label: "Don't import these", value: RULE_SKIP, color: 'Black' },
	],
}];

/**
 * The value a stored rule puts in the picker. A category id stays a number, because that is what
 * CategorySelect matches its options on -- a string would leave the picker looking empty while the
 * rule was in fact set.
 */
export function valueOf(map) {
	if(!isLive(map)) {
		return RULE_NONE;
	}
	if(NON_CATEGORY_RULES.includes(map.action)) {
		return map.action;
	}
	return map.category_id === null ? RULE_NONE : map.category_id;
}

/**
 * Turns what the picker returned into the request that records it. Everything that is not a category
 * of this budget either clears the rule or replaces it with one of the non-category actions --
 * which is why a choice can be saved on the spot rather than needing a confirming click: nothing
 * here writes to the ledger, and re-picking is the undo.
 */
export function applyChoice(batchId, externalCategory, externalMerchant, value) {
	const merchant = externalMerchant || '';
	/** The blank option, whichever label it was given -- on a merchant it means "follow the category". */
	if(!value) {
		return clearImportCategoryMap(batchId, externalCategory, merchant);
	}
	if(NON_CATEGORY_RULES.includes(value)) {
		return saveImportCategoryMap(batchId, externalCategory, merchant, null, value);
	}
	return saveImportCategoryMap(batchId, externalCategory, merchant, Number(value), 'assign');
}

/**
 * The same translation for one transaction's override. The blank option hands the row back to the
 * rules rather than leaving it undecided.
 */
export function applyRowChoice(batchId, externalId, value) {
	if(!value) {
		return clearImportRowOverride(batchId, externalId);
	}
	if(NON_CATEGORY_RULES.includes(value)) {
		return saveImportRowOverride(batchId, externalId, null, value);
	}
	return saveImportRowOverride(batchId, externalId, Number(value), 'assign');
}

/**
 * A category is decided when a rule covers the whole of it, or when every merchant in it has one of
 * its own. Anything short of that leaves rows blocked, which is what the screen's count reports.
 * Rows decided one at a time count too: a line whose every row has an override needs no rule.
 *
 * A merchantless group cannot hold a rule of its own -- '' is the key the whole-category rule is
 * stored under -- so a category containing one is decided by its own rule, or by overrides.
 */
export function isDecided(category) {
	if(isLive(category.map) || isFullyOverridden(category)) {
		return true;
	}
	const merchants = category.merchants || [];
	return merchants.length > 0
		&& merchants.every((merchant) => isLive(merchant.map) || isFullyOverridden(merchant));
}

/** Whether every row of a mapper line has been decided one at a time. */
export function isFullyOverridden(line) {
	return line.row_count > 0 && (line.override_count || 0) >= line.row_count;
}

/**
 * A rule that still decides its rows. One assigning to a category deleted since is kept on the
 * server, so restoring the category brings it back, but it blocks its rows until then -- so it
 * counts as no rule here, and the picker shows it blank.
 */
export function isLive(map) {
	return !!map && !map.category_deleted;
}
